import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const word = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1], delay: 0.1 + i * 0.08 },
  }),
};

const HeroLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      variants={word}
      initial="hidden"
      animate="show"
      custom={delay}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        {/* Top meta row */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3 flex items-start">
            <div className="font-mono-mini text-[#8A8A8A]">
              <div>Portfolio</div>
              <div className="text-[#0A0A0A] mt-1">2022 / 2026</div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6, ease: [0.65, 0, 0.35, 1] }}
              className="text-base md:text-lg leading-relaxed text-[#0A0A0A] max-w-md"
            >
              A UI/UX designer working across product and brand. I build
              high-converting marketing pages and streamlined event flows for
              teams who care how their software feels in a user's hands.
            </motion.p>
          </div>
        </div>

        {/* Massive type statement */}
        <h1
          data-testid="hero-heading"
          className="font-display text-[18vw] md:text-[14vw] lg:text-[11.5vw] leading-[0.86] tracking-tighter text-[#0A0A0A]"
        >
          <HeroLine delay={0}>Design</HeroLine>
          <HeroLine delay={1}>
            <span className="relative">
              that
              <span
                aria-hidden
                className="inline-block w-[0.5em] h-[0.18em] bg-[#FF3B30] mx-[0.18em] align-[0.18em]"
              />
              listens
            </span>
          </HeroLine>
          <HeroLine delay={2}>
            <span className="italic font-light" style={{ fontFamily: "'Cabinet Grotesk', serif" }}>
              before it speaks.
            </span>
          </HeroLine>
        </h1>

        {/* Bottom meta */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-12 border-t border-black/10 pt-8"
        >
          <div className="col-span-6 md:col-span-3 font-mono-mini text-[#8A8A8A]">
            <div>Discipline</div>
            <div className="text-[#0A0A0A] mt-1 normal-case tracking-normal text-sm md:text-base font-medium">
              UX · UI · Interaction
            </div>
          </div>
          <div className="col-span-6 md:col-span-3 font-mono-mini text-[#8A8A8A]">
            <div>Currently</div>
            <div className="text-[#0A0A0A] mt-1 normal-case tracking-normal text-sm md:text-base font-medium">
              Designer at Nunify
            </div>
          </div>
          <div className="col-span-12 md:col-span-3 font-mono-mini text-[#8A8A8A]">
            <div>Clients</div>
            <div className="text-[#0A0A0A] mt-1 normal-case tracking-normal text-sm md:text-base font-medium">
              Nunify, Provus, 2DOT47
            </div>
          </div>
          <div className="col-span-12 md:col-span-3 flex md:justify-end items-end">
            <a
              href="#work"
              data-testid="hero-cta-work"
              className="group inline-flex items-center gap-2 font-mono-mini text-[#0A0A0A]"
            >
              <span className="link-underline-static">Selected Work</span>
              <ArrowDownRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1"
              />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
